/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { VideoEffectPanelContent } from './VideoEffectPanelContent';
import { BackgroundImage, BlurEffect } from './videoEffectTypes';
import { VideoEffectStore } from './store/videoEffectModel';

interface VideoEffectsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  applyBlur: (effect: BlurEffect) => Promise<void>;
  applyBackground: (background: BackgroundImage | null) => Promise<void>;
  removeAllEffects: () => Promise<void>;
  videoContainerRef?: React.RefObject<HTMLElement>;
  rightPanelRef?: React.RefObject<HTMLElement>;
}

const MOTION_INITIAL = { opacity: 0, x: 24 } as const;
const MOTION_ANIMATE = { opacity: 1, x: 0 } as const;
const MOTION_EXIT = { opacity: 0, x: 24 } as const;
const MOTION_TRANSITION = { duration: 0.12, ease: [0.25, 0.1, 0.25, 1] } as const;

const OVERLAY_SHELL_STYLE: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'auto',
};

const OVERLAY_INNER_STYLE: React.CSSProperties = {
  width: '100%',
  height: '100%',
  overflowY: 'auto',
};

const FLOAT_SHELL_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: '100%',
  height: '100%',
  width: 'clamp(360px, 22vw, 420px)',
  pointerEvents: 'auto',
};
const FLOAT_SHELL_ZINDEX_CSS = `
  .video-effects-float-shell {
    z-index: 10;
  }
  @media (max-width: 768px) {
    .video-effects-float-shell {
      z-index: 120;
    }
  }
`;

const FLOAT_INNER_STYLE: React.CSSProperties = {
  width: '100%',
  height: '100%',
  overflowY: 'auto',
};

interface EffectSnapshot {
  blurEffect: BlurEffect | null;
  backgroundId: string | null;
}

export const VideoEffectsPanel = ({
  isOpen,
  onClose,
  applyBlur,
  applyBackground,
  removeAllEffects,
  videoContainerRef,
  rightPanelRef,
}: VideoEffectsPanelProps): JSX.Element => {
  const isApplying = VideoEffectStore.useStoreState((state) => state.isApplyingEffect);
  const activeBlurEffect = VideoEffectStore.useStoreState((state) => state.activeBlurEffect);
  const activeBackgroundId = VideoEffectStore.useStoreState((state) => state.activeBackgroundId);

  const snapshotOnOpen = useRef<EffectSnapshot>({
    blurEffect: activeBlurEffect,
    backgroundId: activeBackgroundId,
  });

  useEffect(
    () => {
      if (!isOpen) return;
      snapshotOnOpen.current = {
        blurEffect: activeBlurEffect,
        backgroundId: activeBackgroundId,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen],
  );

  const portalTarget = rightPanelRef?.current ?? videoContainerRef?.current ?? null;
  const isOverlay = Boolean(rightPanelRef?.current);
  const shellStyle = isOverlay ? OVERLAY_SHELL_STYLE : FLOAT_SHELL_STYLE;
  const innerStyle = isOverlay ? OVERLAY_INNER_STYLE : FLOAT_INNER_STYLE;

  const handleBlurSelect = useCallback(
    (effect: BlurEffect) => {
      if (isApplying) return;
      const isToggleOff = effect === activeBlurEffect && activeBlurEffect !== 'none';
      if (isToggleOff) {
        removeAllEffects();
      } else {
        applyBlur(effect);
      }
    },
    [isApplying, activeBlurEffect, applyBlur, removeAllEffects],
  );

  const handleBackgroundSelect = useCallback(
    (background: BackgroundImage | null) => {
      if (isApplying) return;
      applyBackground(background);
    },
    [isApplying, applyBackground],
  );

  const handleBackgroundRemove = useCallback(() => handleBackgroundSelect(null), [handleBackgroundSelect]);

  const panel = (
    <AnimatePresence>
      {isOpen && (
        <div style={shellStyle} className="video-effects-float-shell">
          <style>{FLOAT_SHELL_ZINDEX_CSS}</style>

          <motion.div
            key="effects-panel"
            initial={MOTION_INITIAL}
            animate={MOTION_ANIMATE}
            exit={MOTION_EXIT}
            transition={MOTION_TRANSITION}
            style={innerStyle}
          >
            <VideoEffectPanelContent
              onBlurSelect={handleBlurSelect}
              onBackgroundSelect={handleBackgroundSelect}
              onBackgroundRemove={handleBackgroundRemove}
              onClose={onClose}
            />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return portalTarget ? (ReactDOM.createPortal(panel, portalTarget) as JSX.Element) : panel;
};
